import React, { useState } from "react";
import { createPortal } from "react-dom";
import { Share2, Copy, Check, X } from "lucide-react";
import useEscape from "../lib/useEscape";
import { toast } from "sonner";

/**
 * Share a deal via WhatsApp, Instagram, Facebook or copy link.
 *
 * Two usage modes:
 *  1. Self-contained trigger: <ShareButton url=... title=... />
 *  2. Controlled (e.g. modal-only): <ShareButton defaultOpen onClose={() => ...} />
 */
export default function ShareButton({
  url,
  title,
  subtitle,
  label = "Share deal",
  testId = "share-deal-btn",
  variant = "primary",
  defaultOpen = false,
  onClose,
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [copied, setCopied] = useState(false);

  const closeModal = () => {
    setOpen(false);
    if (typeof onClose === "function") onClose();
  };
  useEscape(open, closeModal);

  const fullUrl = url || (typeof window !== "undefined" ? window.location.href : "");
  const text = `${title}\n${subtitle ? subtitle + "\n" : ""}Join us on BHEGA → ${fullUrl}`;

  const onWhatsapp = () => {
    const wa = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(wa, "_blank", "noopener");
  };

  const onFacebook = async () => {
    // Facebook sharer.php only accepts the URL; copy the full text so the user can paste into inbox/story.
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Caption copied — paste it into your Facebook post, story or inbox");
    } catch {
      /* clipboard may be blocked; proceed silently */
    }
    const fb = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(fullUrl)}&quote=${encodeURIComponent(text)}`;
    window.open(fb, "_blank", "noopener,width=600,height=520");
  };

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Message copied — paste anywhere to share");
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error("Failed to copy");
    }
  };

  const onInstagram = async () => {
    // Instagram doesn't allow direct link-share. Provide story-friendly copy + deep link.
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Caption copied — open Instagram and paste in your Story/DM");
    } catch {
      toast.error("Could not copy caption");
    }
    window.open("https://www.instagram.com/", "_blank", "noopener");
  };

  const triggerClass = variant === "primary" ? "btn-primary" : "btn-outline";

  const modal = open ? (
    <div className="fixed inset-0 z-[100] bg-background/50 flex items-center justify-center p-4" onClick={closeModal}>
      <div
        className="bg-card w-full max-w-sm rounded-none border border-border shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="share-modal"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h3 className="font-heading font-bold text-base">Share this Bhega ✊</h3>
          <button onClick={closeModal} data-testid="share-modal-close" className="p-1 hover:bg-foreground/10 rounded">
            <X size={16} />
          </button>
        </div>
        <div className="px-5 py-4 space-y-2 text-sm">
          <p className="text-muted-foreground text-xs">Invite friends in your city to join the group and crack the bulk price.</p>

          <button onClick={onWhatsapp} className="w-full flex items-center gap-3 px-3 py-3 border border-border rounded-none hover:border-[#16845B] hover:bg-[#16845B]/5 transition-colors" data-testid="share-whatsapp">
            <span className="w-9 h-9 bg-[#16845B] flex items-center justify-center text-white font-bold">W</span>
            <span className="text-left flex-1">
              <div className="font-semibold">Share on WhatsApp</div>
              <div className="text-xs text-muted-foreground">Open chat with prefilled message</div>
            </span>
          </button>

          <button onClick={onInstagram} className="w-full flex items-center gap-3 px-3 py-3 border border-border rounded-none hover:border-[#E1306C] hover:bg-[#E1306C]/5 transition-colors" data-testid="share-instagram">
            <span className="w-9 h-9 bg-gradient-to-br from-[#feda75] via-[#d62976] to-[#4f5bd5] flex items-center justify-center text-white font-bold">IG</span>
            <span className="text-left flex-1">
              <div className="font-semibold">Share on Instagram</div>
              <div className="text-xs text-muted-foreground">Copy caption & open Instagram</div>
            </span>
          </button>

          <button onClick={onFacebook} className="w-full flex items-center gap-3 px-3 py-3 border border-border rounded-none hover:border-[#1877F2] hover:bg-[#1877F2]/5 transition-colors" data-testid="share-facebook">
            <span className="w-9 h-9 bg-[#1877F2] flex items-center justify-center text-white font-bold">f</span>
            <span className="text-left flex-1">
              <div className="font-semibold">Share on Facebook</div>
              <div className="text-xs text-muted-foreground">Open Facebook share dialog</div>
            </span>
          </button>

          <button onClick={onCopy} className="w-full flex items-center gap-3 px-3 py-3 border border-border rounded-none hover:border-foreground/30 transition-colors" data-testid="share-copy-link">
            <span className="w-9 h-9 bg-card rounded-full flex items-center justify-center text-foreground">
              {copied ? <Check size={14} /> : <Copy size={14} />}
            </span>
            <span className="text-left flex-1 min-w-0">
              <div className="font-semibold">{copied ? "Link copied!" : "Copy link"}</div>
              <div className="text-xs text-muted-foreground truncate">{fullUrl}</div>
            </span>
          </button>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <>
      {!defaultOpen && (
        <button onClick={() => setOpen(true)} className={`${triggerClass} text-sm`} data-testid={testId}>
          <Share2 size={14} /> {label}
        </button>
      )}
      {modal && typeof document !== "undefined" ? createPortal(modal, document.body) : modal}
    </>
  );
}
